import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import { Check } from '@mui/icons-material';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import i18n, { TFunction } from 'i18next';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import RotatingLoader from '../ui/RotatingLoader';
import { useLanguage } from '../utils/GeneralUtils';
import {
  AccountData,
  AmendOrderStatus,
  DateAvailabilityOut,
  OrderData,
  OrderDetailsRequestData,
  OrderService,
  PaymentLinkResponseDTO,
  PaymentLinkService,
  PolicyTypes,
  ReschedulePenaltyData,
  ReschedulePenaltyStatus,
  ReschedulePolicyData,
  RescheduleQuoteData,
} from '../api';
import { ApiError } from '../api/core/ApiError';
import DateAndTimeStep from '../orderPage/steps/dateAndTimeStep/DateAndTimeStep';
import PaymentIframe from '../orderPage/steps/PaymentIframe';
import { useStripeLinkRenderProps } from '../orderPage/steps/payments/useStripeRenderProps';
import { bookingFeeAmount, isBookingFeeOn, quotePrice, withBookingFee } from '../orderPage/core/pricing';
import { customerFacingPriceFormatter } from '../orderPage/utils';
import { CollectOrderDetailsStep } from './CollectOrderDetailsStep';
import { OrderPageIntent } from '../orderPage/OrderPageIntent';
import { getOrderPage } from '../utils/AccountUtils';
import { AccountLogo } from '../shared/ui/AccountLogo';
import { LanguageSwitcher } from '../shared/ui/LanguageSwitcher';
import { phoneMediaQuery, rescheduleTheme, useRubikFont } from './rescheduleTheme';
import { WizardFrame } from './components/WizardFrame';
import { Stepper } from './components/Stepper';
import { InfoTiles, orderToInfoTiles } from './components/InfoTiles';
import { PolicyStrip } from './components/PolicyStrip';
import { PolicyNotice } from './components/PolicyNotice';
import { Notice } from './components/Notice';
import { FromToCards } from './components/FromToCards';
import { Countdown } from './components/Countdown';
import { PrimaryButton } from './components/PrimaryButton';
import { GhostButton } from './components/GhostButton';
import { TextButton } from './components/TextButton';
import { BlockedState } from './components/BlockedState';
import { RescheduleTypography, rescheduleTypographyDefaults } from './components/RescheduleTypography';

type WizardStep = 'lookup' | 'overview' | 'date' | 'review' | 'pay' | 'done';
type PaymentOutcome = 'applied' | 'slot_lost' | 'pending';
type WizardError = { title: string; body: string };
type CompletedChange = { date: string; time: number; fee: number };

const OUTCOME_POLL_INTERVAL_MILLISECONDS = 3000;
const OUTCOME_POLL_LIMIT_MILLISECONDS = 60000;
const PAYMENT_STEP_INDEX = 2;
const STEP_INDEX: Partial<Record<WizardStep, number>> = { date: 0, review: 1, pay: PAYMENT_STEP_INDEX };

const formatDay = (date: string) => moment(date).format('ddd D MMM');
const formatClock = (secondsFromMidnight: number) => moment.unix(secondsFromMidnight).utc().format('H:mm');
const formatWhen = (date: string, time: number) => `${formatDay(date)}, ${formatClock(time)}`;

function mapAmendError(detail: unknown, t: TFunction): WizardError {
  const errorCode = typeof detail === 'string' ? detail : '';
  let body = t('manage_booking.errors.amendFailed');
  if (errorCode.startsWith('rescheduleWindowPassed')) {
    body = t('manage_booking.errors.windowPassed');
  } else if (errorCode.startsWith('slotUnavailable:')) {
    body = t('manage_booking.errors.slotUnavailable');
  } else if (errorCode.startsWith('orderAlreadyStarted')) {
    body = t('manage_booking.errors.orderPassed');
  } else if (errorCode.startsWith('rescheduleNotAllowedFor')) {
    body = t('manage_booking.amendingNotAllowed');
  } else if (errorCode.startsWith('selfServeRescheduleDisabled')) {
    body = t('manage_booking.selfServeDisabled');
  }
  return { title: t('manage_booking.v2.errTitle'), body };
}

const formatTier = (quote: RescheduleQuoteData, account: AccountData) =>
  quote.tier_policy_type === PolicyTypes.PERCENT
    ? `${quote.tier_value}%`
    : customerFacingPriceFormatter(quote.tier_value ?? 0, account);

const LtrText = ({ children }: { children: ReactNode }) => (
  <RescheduleTypography component="span" dir="ltr" style={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
    {children}
  </RescheduleTypography>
);

const SummaryRow = ({ label, value, isLast }: { label: string; value: ReactNode; isLast?: boolean }) => (
  <Grid
    container
    flexWrap="nowrap"
    justifyContent="space-between"
    alignItems="flex-start"
    sx={{
      gap: '16px',
      padding: '14px 0',
      borderBottom: isLast ? undefined : `1px solid ${rescheduleTheme.divider}`,
    }}>
    <RescheduleTypography style={{ fontSize: 15, color: rescheduleTheme.textMuted }}>{label}</RescheduleTypography>
    <RescheduleTypography style={{ fontSize: 15, fontWeight: 500, textAlign: 'end' }}>{value}</RescheduleTypography>
  </Grid>
);

const SummaryCard = ({ children }: { children: ReactNode }) => (
  <Grid
    component="section"
    container
    flexDirection="column"
    flexWrap="nowrap"
    sx={{
      border: `1px solid ${rescheduleTheme.border}`,
      borderRadius: `${rescheduleTheme.radii.card}px`,
      padding: '6px 20px',
    }}>
    {children}
  </Grid>
);

export const ManageBookingWizard = () => {
  useRubikFont();
  const posthog = usePostHog();
  const isPhone = useMediaQuery(phoneMediaQuery);

  const { t } = useTranslation();
  const language = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = searchParams.get('orderid');
  const paidLinkId = searchParams.get('paid');

  const [step, setStep] = useState<WizardStep>('lookup');
  const [order, setOrder] = useState<OrderData | undefined>();
  const [account, setAccount] = useState<AccountData | undefined>();
  const [quote, setQuote] = useState<RescheduleQuoteData | undefined>();
  const [isLoadingOrder, setIsLoadingOrder] = useState(!!orderId);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [availableDates, setAvailableDates] = useState<DateAvailabilityOut[]>([]);
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [date, setDate] = useState<string | undefined | null>(order?.date);
  const [time, setTime] = useState<number | undefined | null>(order?.time);
  const [newPrice, setNewPrice] = useState(order?.price);
  const [isPriceMismatch, setIsPriceMismatch] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [paymentLinkResponse, setPaymentLinkResponse] = useState<PaymentLinkResponseDTO | undefined>();
  const [isPaymentExpired, setIsPaymentExpired] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<WizardError | null>(null);
  const [outcome, setOutcome] = useState<PaymentOutcome | undefined>();
  const [completedChange, setCompletedChange] = useState<CompletedChange | undefined>();
  const [outcomePenalty, setOutcomePenalty] = useState<ReschedulePenaltyData | undefined>();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { stripeAccountId, stripe_integration_publishable_key } = useStripeLinkRenderProps(
    account,
    paymentLinkResponse?.payment_link.link,
  );

  const orderPage = getOrderPage(account, order?.order_page_id || '');

  const loadOrder = async (id: string) => {
    setIsLoadingOrder(true);
    try {
      const retrievedOrder = await OrderService.fetchOrderById(id);
      const retrievedQuote = await OrderService.getRescheduleQuote(id);
      setOrder(retrievedOrder.order as OrderData);
      setAccount(retrievedOrder.account as AccountData);
      setQuote(retrievedQuote);
      if (!paidLinkId) {
        setStep('overview');
      }
    } catch (error) {
      setLookupFailed(true);
    }
    setIsLoadingOrder(false);
  };

  useEffect(() => {
    if (orderId) {
      void loadOrder(orderId);
    }
  }, [orderId]);

  useEffect(() => {
    if (order?.language) {
      void i18n.changeLanguage(order.language);
      document.body.dir = i18n.dir();
    }
  }, [order?.language]);

  useEffect(() => {
    if (!orderId || !paidLinkId) {
      return undefined;
    }
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let isCancelled = false;
    const startedAt = Date.now();
    const pollOutcome = async () => {
      try {
        const polledOrder = await OrderService.fetchOrderById(orderId);
        const penalty = (polledOrder.order as OrderData).reschedule_penalties?.find(
          (reschedulePenalty) => reschedulePenalty.payment_link_id === paidLinkId,
        );
        if (isCancelled) {
          return;
        }
        if (penalty) {
          setOrder(polledOrder.order as OrderData);
          setAccount(polledOrder.account as AccountData);
          setOutcomePenalty(penalty);
          if (penalty.status === ReschedulePenaltyStatus.APPLIED) {
            setCompletedChange({ date: penalty.to_date, time: penalty.to_time, fee: penalty.amount });
            setStep('done');
            setOutcome('applied');
          } else {
            setStep('overview');
            setOutcome('slot_lost');
          }
          return;
        }
      } catch (error) {
        console.error(error);
      }
      if (isCancelled) {
        return;
      }
      if (Date.now() - startedAt >= OUTCOME_POLL_LIMIT_MILLISECONDS) {
        setStep('overview');
        setOutcome('pending');
        return;
      }
      timeoutId = setTimeout(() => {
        void pollOutcome();
      }, OUTCOME_POLL_INTERVAL_MILLISECONDS);
    };
    void pollOutcome();
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [orderId, paidLinkId]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [step, isPaymentExpired, isLoadingOrder]);

  const orderDetails = {
    persons: order?.persons,
    duration: order?.duration,
    price: order?.price,
    rooms: order?.rooms,
    custom_details: order?.custom_details,
  };

  useEffect(() => {
    void (async () => {
      if (order && account) {
        setIsLoadingDates(true);

        const response = await OrderService.getAvailableDates({
          account_id: account.id,
          order_page_id: order.order_page_id,
          // @ts-ignore
          order_details: orderDetails,
        });
        setAvailableDates(response);
        setIsLoadingDates(false);
      }
    })();
  }, [order]);

  const originalDatePrice =
    order && orderPage
      ? quotePrice({
          orderPage,
          details: orderDetails as OrderDetailsRequestData,
          dateDetails: { date: order.date },
          account,
        }).total
      : order?.price;

  const openLookedUpOrder = async (lookedUpOrder: OrderData, lookedUpAccount: AccountData) => {
    setLookupFailed(false);
    setOrder(lookedUpOrder);
    setAccount(lookedUpAccount);
    setQuote(await OrderService.getRescheduleQuote(lookedUpOrder.id));
    setStep('overview');
  };

  const goToOverview = () => {
    setError(null);
    setAgreed(false);
    setIsPaymentExpired(false);
    setPaymentLinkResponse(undefined);
    setStep('overview');
  };

  const reloadOverview = async () => {
    if (!order) {
      return;
    }
    if (paidLinkId) {
      setSearchParams({ orderid: order.id }, { replace: true });
    }
    setOutcome(undefined);
    setOutcomePenalty(undefined);
    setCompletedChange(undefined);
    goToOverview();
    await loadOrder(order.id);
  };

  const goToDateStep = () => {
    setError(null);
    setIsPriceMismatch(false);
    setIsPaymentExpired(false);
    setPaymentLinkResponse(undefined);
    setStep('date');
  };

  const continueFromDateStep = () => {
    if (!date || !time) {
      return;
    }
    if (newPrice !== originalDatePrice) {
      setIsPriceMismatch(true);
      posthog.capture('Reschedule Blocked Price Difference', {
        account_id: account?.id,
        order_page_id: order?.order_page_id,
        order_id: order?.id,
        from_date: order?.date,
        to_date: date,
        original_price: originalDatePrice,
        new_price: newPrice,
      });
      return;
    }
    setIsPriceMismatch(false);
    setError(null);
    setAgreed(false);
    setStep('review');
  };

  const confirmChange = async () => {
    if (!order || !date || !time) {
      return;
    }
    const payload = { order_id: order.id, date, time };
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await OrderService.amendOrder(payload);
      posthog.capture('Order Edited', { editor: 'client', payload, fee: response.fee });
      if (response.status === AmendOrderStatus.APPLIED) {
        if (response.order) {
          setOrder(response.order);
        }
        setCompletedChange({ date, time, fee: response.fee });
        setStep('done');
      } else {
        const builtPaymentLink = await PaymentLinkService.buildPaymentLink({
          payment_link_id: response.payment_link_id!,
        });
        setPaymentLinkResponse(builtPaymentLink);
        setIsPaymentExpired(false);
        setStep('pay');
      }
    } catch (caughtError) {
      setError(mapAmendError((caughtError as ApiError)?.body?.detail, t));
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepHeading = (text: string, fontSize = 22) => (
    <Typography
      ref={headingRef}
      component="h2"
      tabIndex={-1}
      sx={{ ...rescheduleTypographyDefaults, fontSize: `${fontSize}px`, fontWeight: 600, outline: 'none' }}>
      {text}
    </Typography>
  );

  const logo = (
    <AccountLogo account={account} width={300} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="" />
  );
  const frameTitle = orderPage?.label?.[language] ?? '';

  if (isLoadingOrder || (paidLinkId && !outcome && !lookupFailed)) {
    return (
      <Grid item sx={{ height: '500px' }}>
        <RotatingLoader />
      </Grid>
    );
  }

  if (step === 'lookup' || !order || !account || !quote) {
    return (
      <WizardFrame logo={null} title="" compactHeader>
        <CollectOrderDetailsStep setNotFound={() => setLookupFailed(true)} setOrder={openLookedUpOrder} />
        {lookupFailed && <Notice variant="error" title={t('manage_booking.errors.orderNotFound')} body="" />}
      </WizardFrame>
    );
  }

  const isFeeChange = quote.fee > 0;
  const isInsideFeeTier = quote.tier_hours_before != null;
  const orderNumberText = t('manage_booking.v2.orderNumber', { n: order.order_number });
  const currentWhen = formatWhen(order.date!, order.time ?? 0);
  const newWhen = date && time ? formatWhen(date, time) : '';
  const formattedFee = customerFacingPriceFormatter(quote.fee, account);
  const hasBookingFee = isBookingFeeOn(account) && isFeeChange;
  const formattedBookingFee = customerFacingPriceFormatter(bookingFeeAmount(quote.fee, account), account);
  const formattedTotal = customerFacingPriceFormatter(withBookingFee(quote.fee, account), account);
  const formattedAmountToPay = hasBookingFee ? formattedTotal : formattedFee;
  const roundedHoursUntilStart = Math.round(quote.hours_until_start);
  const reschedulePolicy: ReschedulePolicyData | undefined = orderPage?.effective_reschedule_policy;
  const tierHoursBefore = (reschedulePolicy?.fee_tiers ?? []).map((tier) => tier.hours_before);
  const freeAboveHours = tierHoursBefore.length > 0 ? Math.max(...tierHoursBefore) : quote.min_hours_before;
  const stepperSteps = [
    { key: 'date', label: t('manage_booking.v2.stepDate') },
    { key: 'review', label: t('manage_booking.v2.stepReview') },
    ...(isFeeChange ? [{ key: 'pay', label: t('manage_booking.v2.stepPay') }] : []),
  ];
  const isCompactStep = isPhone && (step === 'date' || step === 'review' || step === 'pay');
  const stepIndex = STEP_INDEX[step];
  const stepper = stepIndex !== undefined && <Stepper steps={stepperSteps} currentIndex={stepIndex} />;
  const errorNotice = error && <Notice variant="error" title={error.title} body={error.body} />;

  const renderContent = (): { content: ReactNode; actions?: ReactNode } => {
    if (outcome === 'slot_lost' && outcomePenalty) {
      return {
        content: (
          <>
            <InfoTiles {...orderToInfoTiles(order, t)} />
            <Notice
              variant="error"
              title={t('manage_booking.v2.slotLostTitle', {
                when: formatWhen(outcomePenalty.to_date, outcomePenalty.to_time),
              })}
              body={t('manage_booking.v2.slotLostBody', {
                fee: customerFacingPriceFormatter(outcomePenalty.amount, account),
              })}
            />
            <GhostButton
              onClick={() => {
                void reloadOverview();
              }}>
              {t('manage_booking.v2.viewMyBooking')}
            </GhostButton>
          </>
        ),
      };
    }

    if (outcome === 'pending') {
      return {
        content: (
          <>
            <Notice
              variant="info"
              title={t('manage_booking.v2.confirming')}
              body={t('manage_booking.v2.confirmingBody')}
            />
            <GhostButton
              onClick={() => {
                void reloadOverview();
              }}>
              {t('manage_booking.v2.viewMyBooking')}
            </GhostButton>
          </>
        ),
      };
    }

    if (step === 'overview') {
      return {
        content: (
          <>
            <Grid container flexWrap="nowrap" justifyContent="space-between" alignItems="center">
              {stepHeading(t('manage_booking.v2.yourBooking'), 20)}
              <RescheduleTypography style={{ fontSize: 14, color: rescheduleTheme.textMuted }}>
                {orderNumberText}
              </RescheduleTypography>
            </Grid>
            {quote.allowed ? (
              <>
                <InfoTiles {...orderToInfoTiles(order, t)} />
                {reschedulePolicy && (
                  <Grid component="section" container flexDirection="column" flexWrap="nowrap" gap="14px">
                    <RescheduleTypography variant="h3" style={{ fontSize: 20, fontWeight: 600 }}>
                      {t('manage_booking.v2.changePolicy')}
                    </RescheduleTypography>
                    <PolicyStrip
                      policy={reschedulePolicy}
                      currentTierHoursBefore={quote.tier_hours_before}
                      account={account}
                    />
                  </Grid>
                )}
                <PolicyNotice
                  variant={isInsideFeeTier ? 'fee' : 'free'}
                  text={
                    isInsideFeeTier
                      ? t(
                          quote.tier_policy_type === PolicyTypes.PERCENT
                            ? 'manage_booking.v2.noticeFee'
                            : 'manage_booking.v2.noticeFeeFixed',
                          {
                            h: roundedHoursUntilStart,
                            tier: formatTier(quote, account),
                            fee: formattedFee,
                          },
                        )
                      : t('manage_booking.v2.noticeFree', { h: freeAboveHours })
                  }
                />
                <PrimaryButton onClick={goToDateStep}>{t('manage_booking.v2.ctaChange')}</PrimaryButton>
              </>
            ) : (
              <BlockedState
                reason={quote.reason ?? ''}
                order={order}
                account={account}
                reschedulePolicy={reschedulePolicy}
              />
            )}
          </>
        ),
      };
    }

    if (step === 'date') {
      return {
        content: (
          <>
            {stepper}
            <Grid container flexDirection="column" flexWrap="nowrap" gap="4px">
              {stepHeading(t('manage_booking.v2.pickTitle'))}
              <RescheduleTypography style={{ fontSize: 15, color: rescheduleTheme.textMuted }}>
                {t('manage_booking.v2.nowBooked', { when: currentWhen })}
              </RescheduleTypography>
            </Grid>
            <DateAndTimeStep
              account={account}
              initialAvailableDates={availableDates}
              locations={account.locations || []}
              orderPage={orderPage}
              isLoadingDates={isLoadingDates}
              availableDates={availableDates}
              orderPageIntent={OrderPageIntent.MANAGE_BOOKING}
              // @ts-ignore
              orderDetails={orderDetails}
              setDateTimeAndPrice={({ newDate, newTime, newPrice }) => {
                setTime(newTime);
                setDate(newDate);
                setNewPrice(newPrice);
                setIsPriceMismatch(false);
              }}
              setTime={setTime}
              date={date}
              time={time}
            />
            {isPriceMismatch && (
              <Notice
                variant="error"
                title={t('manage_booking.v2.errTitle')}
                body={t('manage_booking.v2.priceDiffers')}
              />
            )}
          </>
        ),
        actions: (
          <>
            <GhostButton onClick={goToOverview}>{t('manage_booking.v2.back')}</GhostButton>
            <PrimaryButton disabled={!date || !time} onClick={continueFromDateStep}>
              {t('manage_booking.v2.continue')}
            </PrimaryButton>
          </>
        ),
      };
    }

    if (step === 'review') {
      const feeBasis = t('manage_booking.v2.feeBasis', {
        h: roundedHoursUntilStart,
        basis:
          quote.tier_policy_type === PolicyTypes.PERCENT
            ? t('manage_booking.v2.percentOf', {
                p: quote.tier_value,
                price: customerFacingPriceFormatter(quote.base_price, account),
              })
            : formattedFee,
      });
      return {
        content: (
          <>
            {stepper}
            {stepHeading(t('manage_booking.v2.reviewTitle'))}
            <FromToCards fromText={currentWhen} toText={newWhen} />
            <SummaryCard>
              <SummaryRow label={t('manage_booking.v2.participants')} value={order.persons} />
              <SummaryRow
                label={t('manage_booking.v2.workshopPrice')}
                value={`${customerFacingPriceFormatter(
                  hasBookingFee ? withBookingFee(quote.base_price, account) : quote.base_price,
                  account,
                )} · ${t('manage_booking.v2.unchanged')}`}
                isLast={!isFeeChange}
              />
              {isFeeChange ? (
                <Grid
                  container
                  flexWrap="nowrap"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{
                    gap: '16px',
                    padding: '14px 0',
                    borderBottom: hasBookingFee ? `1px solid ${rescheduleTheme.divider}` : undefined,
                  }}>
                  <Grid container flexDirection="column" flexWrap="nowrap" width="auto" gap="3px">
                    <RescheduleTypography style={{ fontSize: 15, fontWeight: 600 }}>
                      {t('manage_booking.v2.lateFee')}
                    </RescheduleTypography>
                    <RescheduleTypography style={{ fontSize: 13, color: rescheduleTheme.textMuted }}>
                      {feeBasis}
                    </RescheduleTypography>
                  </Grid>
                  <RescheduleTypography
                    dir="ltr"
                    style={{ fontSize: 20, fontWeight: 600, color: rescheduleTheme.brand }}>
                    {formattedFee}
                  </RescheduleTypography>
                </Grid>
              ) : (
                <Grid container flexWrap="nowrap" justifyContent="space-between" sx={{ padding: '14px 0' }}>
                  <RescheduleTypography style={{ fontSize: 15, fontWeight: 600 }}>
                    {t('manage_booking.v2.changeFee')}
                  </RescheduleTypography>
                  <RescheduleTypography style={{ fontSize: 15, fontWeight: 600, color: rescheduleTheme.brand }}>
                    {t('manage_booking.v2.free')}
                  </RescheduleTypography>
                </Grid>
              )}
              {hasBookingFee && (
                <>
                  <SummaryRow
                    label={t('manage_booking.v2.bookingFee')}
                    value={<LtrText>{formattedBookingFee}</LtrText>}
                  />
                  <Grid container flexWrap="nowrap" justifyContent="space-between" sx={{ padding: '14px 0' }}>
                    <RescheduleTypography style={{ fontSize: 15, fontWeight: 600 }}>
                      {t('manage_booking.v2.totalToPay')}
                    </RescheduleTypography>
                    <RescheduleTypography
                      dir="ltr"
                      style={{ fontSize: 20, fontWeight: 600, color: rescheduleTheme.brand }}>
                      {formattedTotal}
                    </RescheduleTypography>
                  </Grid>
                </>
              )}
            </SummaryCard>
            {isFeeChange && (
              <Grid container flexDirection="column" flexWrap="nowrap" gap="14px">
                <RescheduleTypography style={{ fontSize: 14, lineHeight: 1.55, color: rescheduleTheme.textMuted }}>
                  {t('manage_booking.v2.refundNote')}
                </RescheduleTypography>
                <Grid
                  component="label"
                  htmlFor="rescheduleConsent"
                  container
                  flexWrap="nowrap"
                  alignItems="flex-start"
                  sx={{ gap: '12px', cursor: 'pointer' }}>
                  <Box
                    component="input"
                    id="rescheduleConsent"
                    type="checkbox"
                    checked={agreed}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAgreed(event.target.checked)}
                    sx={{ width: '20px', height: '20px', flex: 'none', accentColor: rescheduleTheme.brand }}
                  />
                  <RescheduleTypography component="span" style={{ fontSize: 15, lineHeight: 1.45 }}>
                    {hasBookingFee
                      ? t('manage_booking.v2.consentTotal', { total: formattedTotal })
                      : t('manage_booking.v2.consent', { fee: formattedFee })}
                  </RescheduleTypography>
                </Grid>
              </Grid>
            )}
            {errorNotice}
          </>
        ),
        actions: (
          <>
            <GhostButton onClick={goToDateStep}>{t('manage_booking.v2.back')}</GhostButton>
            <PrimaryButton
              disabled={isFeeChange && !agreed}
              loading={isSubmitting}
              onClick={() => {
                void confirmChange();
              }}>
              {isFeeChange
                ? t('manage_booking.v2.continueToPayment', { fee: formattedAmountToPay })
                : t('manage_booking.v2.confirmChange')}
            </PrimaryButton>
          </>
        ),
      };
    }

    if (step === 'pay') {
      const paymentLink = paymentLinkResponse?.payment_link;
      return {
        content: (
          <>
            {stepper}
            <Grid container flexDirection="column" flexWrap="nowrap" gap="4px">
              {stepHeading(t('manage_booking.v2.payTitle'))}
              <RescheduleTypography style={{ fontSize: 15, color: rescheduleTheme.textMuted }}>
                {t('manage_booking.v2.movingTo', { when: newWhen })}
              </RescheduleTypography>
            </Grid>
            {isPaymentExpired ? (
              <>
                <Notice
                  variant="warn"
                  title={t('manage_booking.v2.timeRanOut')}
                  body={t('manage_booking.v2.timeRanOutBody', { when: currentWhen })}
                />
                <PrimaryButton onClick={goToDateStep}>{t('manage_booking.v2.chooseAgain')}</PrimaryButton>
              </>
            ) : (
              <>
                {paymentLink?.expires_at && (
                  <Countdown expiresAt={paymentLink.expires_at} onExpire={() => setIsPaymentExpired(true)} />
                )}
                <Grid
                  component="section"
                  container
                  flexWrap="nowrap"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    gap: '12px',
                    padding: '18px 20px',
                    borderRadius: `${rescheduleTheme.radii.card}px`,
                    background: rescheduleTheme.brandTint,
                  }}>
                  <RescheduleTypography style={{ fontSize: 16, fontWeight: 500 }}>
                    {t('manage_booking.v2.lateFee')} · {orderNumberText}
                  </RescheduleTypography>
                  <RescheduleTypography dir="ltr" bold style={{ fontSize: 24, color: rescheduleTheme.brand }}>
                    {formattedAmountToPay}
                  </RescheduleTypography>
                </Grid>
                {paymentLink?.link && (
                  <PaymentIframe
                    paymentUrl={paymentLink.link}
                    stripe_integration_publishable_key={stripe_integration_publishable_key}
                    stripeAccountId={stripeAccountId}
                    onPaid={() => setSearchParams({ orderid: order.id, paid: paymentLink.id }, { replace: true })}
                    onFormExpired={() => setIsPaymentExpired(true)}
                  />
                )}
                <TextButton onClick={goToOverview}>{t('manage_booking.v2.cancelKeep')}</TextButton>
              </>
            )}
          </>
        ),
      };
    }

    const completed = completedChange;
    return {
      content: (
        <>
          <Grid
            container
            flexDirection="column"
            flexWrap="nowrap"
            alignItems="center"
            sx={{ gap: '22px', textAlign: 'center' }}>
            <Grid
              container
              aria-hidden="true"
              flexWrap="nowrap"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: '76px',
                height: '76px',
                borderRadius: '50%',
                background: rescheduleTheme.brand,
                color: '#FFFFFF',
              }}>
              <Check sx={{ fontSize: '44px' }} />
            </Grid>
            <Grid container flexDirection="column" flexWrap="nowrap" gap="8px">
              {stepHeading(t('manage_booking.v2.doneTitle'), 26)}
              <RescheduleTypography style={{ fontSize: 16, color: rescheduleTheme.textMuted }}>
                {t('manage_booking.v2.doneBody')}
              </RescheduleTypography>
            </Grid>
          </Grid>
          {completed && (
            <SummaryCard>
              <SummaryRow
                label={t('manage_booking.v2.newDate')}
                value={<LtrText>{formatDay(completed.date)}</LtrText>}
              />
              <SummaryRow
                label={t('manage_booking.v2.time')}
                value={<LtrText>{formatClock(completed.time)}</LtrText>}
              />
              <SummaryRow label={t('manage_booking.v2.participants')} value={order.persons} />
              <SummaryRow
                label={t('manage_booking.v2.changeFee')}
                value={
                  completed.fee > 0
                    ? t('manage_booking.v2.feePaid', { fee: customerFacingPriceFormatter(completed.fee, account) })
                    : t('manage_booking.v2.free')
                }
                isLast
              />
            </SummaryCard>
          )}
          <PrimaryButton
            onClick={() => {
              void reloadOverview();
            }}>
            {t('manage_booking.v2.viewBooking')}
          </PrimaryButton>
        </>
      ),
    };
  };

  const { content, actions } = renderContent();

  return (
    <WizardFrame
      logo={logo}
      title={frameTitle}
      compactHeader={isCompactStep}
      headerAction={<LanguageSwitcher languages={account.customer_facing_widgets_languages ?? []} />}
      actions={actions}>
      {content}
    </WizardFrame>
  );
};
